import React, { useState, useEffect } from 'react'
import { deleteIdData, getDataWithStudent, updateData } from '../../lib/studentprogresstask'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import DataTable from 'react-data-table-component'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { globalStudentTaskColumns } from '../../Data/StudentManagement/GlobalStudentTaskData'
import StudentTaskModal from './StudentTaskModal'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { ConfirmModal } from '../Common/ConfirmModal'

const GlobalStudentTask = ({ studentId, type, isStudent }) => {
	const [studentProgressTasks, setStudentProgressTasks] = useState([])
	const [showHiddenTasks, setShowHiddenTasks] = useState(sessionStorage.getItem('hiddenTasks') === 'true' || false)
	const [searchText, setSearchText] = useState('')
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editingTask, setEditingTask] = useState(null)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [deleteId, setDeleteId] = useState(null)

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class']
		})
		return () => observer.disconnect()
	}, [])

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const { user } = useAuth()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	// Override isStudent based on user role
	isStudent = user?.role === 'Instructor' ? false : true

	const handleDelete = (id) => {
		setDeleteId(id)
		setShowConfirmModal(true)
	}

	const confirmDelete = async () => {
		if (deleteId) {
			try {
				await deleteIdData(deleteId)
				showToastMessage('Task deleted successfully!')
				getStudentProgressTasks()
			} catch (error) {
				console.error('Error deleting task:', error)
			} finally {
				setDeleteId(null)
			}
		}
	}

	const handleCompleteChange = async (progressTaskId, currentValue) => {
		const newValue = !currentValue
		try {
			await updateData(progressTaskId, { isFinish: newValue })
			showToastMessage('Task complete status updated!')
			getStudentProgressTasks()
		} catch (error) {
			console.error('Error updating task:', error)
		}
	}

	const handleDismissedChange = async (progressTaskId, currentValue) => {
		const newValue = !currentValue
		try {
			await updateData(progressTaskId, { ishide: newValue })
			showToastMessage('Task visibility updated!')
			getStudentProgressTasks()
		} catch (error) {
			console.error('Error updating task:', error)
		}
	}

	const handleEdit = (task) => {
		setEditingTask(task)
		setIsModalOpen(true)
	}

	const handleAddTask = () => {
		setEditingTask(null)
		setIsModalOpen(true)
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
	}

	const getStudentProgressTasks = async () => {
		try {
			const stasks = await getDataWithStudent(studentId, type)
			setStudentProgressTasks(stasks)
		} catch (error) {
			console.error('Error fetching student progress tasks:', error)
		}
	}

	useEffect(() => {
		if (studentId) {
			getStudentProgressTasks()
		}
	}, [studentId])

	const filteredTasks = studentProgressTasks
		.filter((task) => {
			// Varsayılan görünüm: Dismissed (ishide) veya Complete (isFinish) işaretli satırlar gizlenir
			if (showHiddenTasks) return true
			return !task.ishide && !task.isFinish
		})
		.filter((task) => {
			const name = task.taskname?.toLowerCase() || ''
			const subject = task.subject?.toLowerCase() || ''
			return name.includes(searchText.toLowerCase()) || subject.includes(searchText.toLowerCase())
		})
		.sort((a, b) => {
			if (a.ishide !== b.ishide) {
				return a.ishide ? 1 : -1
			}
			if (a.isFinish !== b.isFinish) {
				return a.isFinish ? 1 : -1
			}
			return (a.taskname || '').localeCompare(b.taskname || '')
		})

	const stickyOffsets = isStudent ? {
		assigned: '340px', // 120 + 110 + 110
		due: '220px',      // 110 + 110
		complete: '110px', // 110
		dismissed: '0px'
	} : {
		assigned: '490px', // 150 + 110 + 110 + 120
		due: '370px',      // 150 + 110 + 110
		complete: '260px', // 150 + 110
		dismissed: '150px', // 150
		actions: '0px'
	}

	const customStyles = {
		headCells: {
			style: {
				fontSize: '16px',
				fontWeight: '600',
				paddingLeft: '12px',
				paddingRight: '12px',
				color: isDarkMode ? '#fff' : '#000',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff',
				'&:nth-child(2)': { position: 'sticky', right: stickyOffsets.assigned, zIndex: 3, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				'&:nth-child(3)': { position: 'sticky', right: stickyOffsets.due, zIndex: 3, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				'&:nth-child(4)': { position: 'sticky', right: stickyOffsets.complete, zIndex: 3, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				'&:nth-child(5)': { position: 'sticky', right: stickyOffsets.dismissed, zIndex: 3, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				...(!isStudent && { '&:nth-child(6)': { position: 'sticky', right: stickyOffsets.actions, zIndex: 3, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' } })
			}
		},
		cells: {
			style: {
				fontSize: '15px',
				paddingLeft: '12px',
				paddingRight: '12px',
				color: isDarkMode ? '#fff' : '#000',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff',
				'&:nth-child(2)': { position: 'sticky', right: stickyOffsets.assigned, zIndex: 2, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				'&:nth-child(3)': { position: 'sticky', right: stickyOffsets.due, zIndex: 2, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				'&:nth-child(4)': { position: 'sticky', right: stickyOffsets.complete, zIndex: 2, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				'&:nth-child(5)': { position: 'sticky', right: stickyOffsets.dismissed, zIndex: 2, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' },
				...(!isStudent && { '&:nth-child(6)': { position: 'sticky', right: stickyOffsets.actions, zIndex: 2, backgroundColor: isDarkMode ? '#2c323f' : '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' } })
			}
		},
		rows: {
			style: {
				minHeight: '50px',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff'
			}
		},
		pagination: {
			style: {
				color: isDarkMode ? '#fff' : '#000',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff'
			}
		}
	}

	const columns = globalStudentTaskColumns(layout, role, studentId, type, isStudent, handleCompleteChange, handleDelete, handleEdit, handleDismissedChange)


	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					<button
						type="button"
						className="btn btn-link p-0 me-2"
						data-bs-toggle="collapse"
						data-bs-target="#collapseTask"
						aria-expanded="true"  // Changed from "false" to "true"
						aria-controls="collapseTask"
					>
						<i className="fa fa-chevron-down"></i>
					</button>
					<h5 className="mb-0 d-inline-block">Task</h5>
				</div>
			</div>
			<div className="collapse show" id="collapseTask">  
				<div className="card-body">
					<div className="row g-3 mb-3">
						<div className="col-md-6">
							<input
								type="text"
								className="form-control"
								placeholder="Search tasks..."
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
							/>
						</div>
						<div className="col-md-6 d-flex gap-2 justify-content-end">
							<button
								onClick={() => {
									const newValue = !showHiddenTasks
									sessionStorage.setItem('hiddenTasks', newValue)
									setShowHiddenTasks(newValue)
								}}
								className={`btn btn-sm ${showHiddenTasks ? 'btn-warning' : 'btn-info'}`}
							>
								<i className="fa fa-eye me-1"></i>
								{showHiddenTasks ? 'Hide Done' : 'Show All'}
							</button>
							{!isStudent && (
								<button
									className="btn btn-primary btn-sm"
									onClick={handleAddTask}
								>
									<i className="fa fa-plus me-1"></i>
									Add Task
								</button>
							)}
						</div>
					</div>
					<div style={{ overflowX: 'auto' }}>
						<DataTable
							columns={columns}
							data={filteredTasks}
							pagination
							highlightOnHover
							responsive
							dense
							noDataComponent="No tasks found."
							customStyles={customStyles}
							defaultSortFieldId="ishide"
							defaultSortAsc={true}
						/>
					</div>
					<StudentTaskModal
						isOpen={isModalOpen}
						toggle={toggleModal}
						studentId={studentId}
						type={type}
						taskToEdit={editingTask}
						onSave={getStudentProgressTasks}
					/>
					<ConfirmModal
						isOpen={showConfirmModal}
						toggle={() => setShowConfirmModal(false)}
						onConfirm={confirmDelete}
						title="Delete Task"
						message="Are you sure you want to delete this task? This action cannot be undone."
					/>
				</div>
			</div>
		</div>
	)
}

export { GlobalStudentTask }
